import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Phone, Mail, MapPin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error'

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="contact-section" className="section-padding bg-background">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-text mb-4 block">{t('contact.label')}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-8">
              {t('contact.title')}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name"
                    placeholder={t('contact.form.name')}
                    className="w-full px-0 py-4 bg-transparent border-0 border-b border-border focus:border-gold text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email"
                    placeholder={t('contact.form.email')}
                    className="w-full px-0 py-4 bg-transparent border-0 border-b border-border focus:border-gold text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="contact-phone"
                    placeholder={t('contact.form.phone')}
                    className="w-full px-0 py-4 bg-transparent border-0 border-b border-border focus:border-gold text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    data-testid="contact-service"
                    className="w-full px-0 py-4 bg-transparent border-0 border-b border-border focus:border-gold text-foreground"
                  >
                    <option value="">{t('contact.form.service')}</option>
                    <option value="kitchen">{t('contact.form.services.kitchen')}</option>
                    <option value="renovation">{t('contact.form.services.renovation')}</option>
                    <option value="consultation">{t('contact.form.services.consultation')}</option>
                    <option value="custom">{t('contact.form.services.custom')}</option>
                  </select>
                </div>
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="contact-message"
                  placeholder={t('contact.form.message')}
                  className="w-full px-0 py-4 bg-transparent border-0 border-b border-border focus:border-gold text-foreground placeholder:text-muted-foreground resize-none"
                />
              </div>

              {/* Status Messages */}
              {status === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-green-600"
                >
                  <CheckCircle size={20} />
                  <span>{t('contact.success')}</span>
                </motion.div>
              )}
              {status === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-red-600"
                >
                  <AlertCircle size={20} />
                  <span>{t('contact.error')}</span>
                </motion.div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-gold hover:text-charcoal transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {t('contact.form.submit')}
                    <Send size={18} className="group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Contact Info & Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            <div className="aspect-video overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1527501034545-bbadafbf308b"
                alt="Our showroom"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-gold" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    {currentLang === 'ar' ? 'اتصل بنا' : 'Call Us'}
                  </h4>
                  <a href="tel:+966593591595" className="text-muted-foreground hover:text-gold transition-colors">
                    +966 593 591 595
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="text-gold" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    {currentLang === 'ar' ? 'البريد الإلكتروني' : 'Email Us'}
                  </h4>
                  <a href="mailto:info@armankitchens.com" className="text-muted-foreground hover:text-gold transition-colors">
                    info@armankitchens.com
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-start sm:col-span-2">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-gold" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    {currentLang === 'ar' ? 'موقعنا' : 'Visit Us'}
                  </h4>
                  <span className="text-muted-foreground">
                    {currentLang === 'ar' ? 'الرياض، المملكة العربية السعودية' : 'Riyadh, Saudi Arabia'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
